import { useCallback, useEffect, useReducer } from 'react';
import { isEmpty } from 'lodash-es';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { getDepositHistory, getWithdrawHistory } from '../../api/history';
import { GetHistoryParams } from '../../api/history/types';
import { PAYMENT_TYPES } from '../../constants';
import {
  setAddHistoryAction,
  setDataTypeAction,
  setIsErrorAction,
  setIsFetchingAction,
  setResetHistoryAction,
  setUpdateHistoryAction
} from '../../store/history/actions';
import {
  historyReducer,
  initialHistoryData
} from '../../store/history/reducer';
import { ApiError } from '../../types/apiError';
import { formatErrorMessage } from '../../utils';
import { Filter } from '../useTransactionsFilter';

export const useTransactionHistory = (
  filter: Filter,
  type: 'deposit' | 'withdraw'
) => {
  const { t } = useI18n();
  const { errorNotification } = useNotifications();

  const isDeposit = type === PAYMENT_TYPES.DEPOSIT;
  const [
    { data, isFetching, isFetchingError, page, totalPages, dataType },
    historyDispatch
  ] = useReducer(historyReducer, initialHistoryData);

  const resetHistory = () => historyDispatch(setResetHistoryAction());

  const getDataRequest = useCallback(() => {
    if (!isEmpty(filter)) {
      historyDispatch(setIsErrorAction(false));
      historyDispatch(setIsFetchingAction());
      historyDispatch(setDataTypeAction(type));

      const reqParams: GetHistoryParams = {
        page: (page as number) + 1,
        ...filter
      };
      return isDeposit
        ? getDepositHistory(reqParams)
        : getWithdrawHistory(reqParams);
    }
    return Promise.resolve(initialHistoryData);
  }, [filter, isDeposit, page, type]);

  const handleError = useCallback(
    (error: ApiError) => {
      const { errorCode } = error;
      errorNotification(t(formatErrorMessage(errorCode)));
      historyDispatch(setIsErrorAction());
      historyDispatch(setIsFetchingAction(false));
    },
    [errorNotification, t]
  );

  const getHistory = useCallback(() => {
    getDataRequest()
      .then((response) => {
        historyDispatch(setAddHistoryAction(response));
      })
      .catch(handleError);
  }, [getDataRequest, handleError]);

  const updateHistory = useCallback(() => {
    getDataRequest()
      .then((response) => {
        historyDispatch(setUpdateHistoryAction(response));
      })
      .catch(handleError);
  }, [getDataRequest, handleError]);

  const requestHistory = page ? updateHistory : getHistory;

  useEffect(() => {
    if (type !== dataType) {
      resetHistory();
    }
  }, [dataType, type]);

  useEffect(() => {
    if (!page && !isFetching && !isFetchingError) {
      requestHistory();
    }
  }, [isFetching, isFetchingError, page, requestHistory]);

  return {
    data,
    page,
    totalPages,
    requestHistory,
    resetHistory,
    dataType,
    isFetching,
    isFetchingError
  };
};
